import { List, Map } from 'immutable';

import { shapeKeywordsForAPI } from '../product/shapeAttributes';
import { tagsToList } from '../tags';
import { keepOld } from '../iterable/keepOld';

import { AI_OPERATION, ATTRIBUTE_TO_DESTINATION } from '../../constants/ai';
import { MAX_NUMBER_OF_TAGS } from '../../constants/validations';
import { OPERATIONS } from '../../constants/bulkEdit';
import { ATTRIBUTE } from '../../constants/attributes';
import { VALUE } from '../../constants/product';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function getAIOperation({ channel, products, value }) {
  const { destination, instructions, operation } = value;
  return Map({
    products,
    type: OPERATIONS[channel].AI,
    value: Map({
      channel,
      destination_field: destination,
      input: Map({ instructions }),
      operation,
    }),
  });
}

export function getAIProfileOperations({ channel, products, profile }) {
  function getAIProfileOperation(attribute) {
    return getAIOperation({
      channel,
      products,
      value: {
        destination: ATTRIBUTE_TO_DESTINATION[attribute] || attribute,
        instructions: profile.getIn(['ai', attribute, 'instructions']),
        operation: profile.getIn(['ai', attribute, 'operation']),
      },
    });
  }

  function reduceSections(operations, value, section) {
    if (!value) return operations;

    switch (section) {
      case FORM.DESCRIPTION: {
        return operations.push(getAIProfileOperation(ATTRIBUTE.DESCRIPTION));
      }

      case FORM.SEO: {
        return operations.push(
          getAIProfileOperation(ATTRIBUTE.META_DESCRIPTION),
          getAIProfileOperation(ATTRIBUTE.PAGE_TITLE),
        );
      }

      case FORM.TAGS: {
        return operations.push(getAIProfileOperation(ATTRIBUTE.TAGS));
      }

      case FORM.TITLE: {
        return operations.push(getAIProfileOperation(ATTRIBUTE.TITLE));
      }

      default: {
        return operations;
      }
    }
  }

  return profile.get('sections').reduce(reduceSections, EMPTY.LIST);
}

export function getProductOperation({ ai, attribute, channel, productId }) {
  function getOperationType() {
    switch (attribute) {
      case ATTRIBUTE.DESCRIPTION: {
        return OPERATIONS[channel].DESCRIPTION.SET;
      }

      case ATTRIBUTE.META_DESCRIPTION: {
        return OPERATIONS[channel].META_DESCRIPTION.SET;
      }

      case ATTRIBUTE.PAGE_TITLE: {
        return OPERATIONS[channel].PAGE_TITLE.SET;
      }

      case ATTRIBUTE.TAGS: {
        return OPERATIONS[channel].TAGS.CHANGE_TO;
      }

      case ATTRIBUTE.TITLE: {
        return OPERATIONS[channel].TITLE.SET;
      }

      case 'keywords': {
        return OPERATIONS[channel].KEYWORDS.SET;
      }

      default: {
        return undefined;
      }
    }
  }

  function getOperationValue() {
    switch (attribute) {
      case ATTRIBUTE.TAGS: {
        switch (ai.get('operation')) {
          case AI_OPERATION.ADD_MAXIMUM: {
            return tagsToList({ channel, tags: ai.get('original').mergeWith(keepOld, ai.get('generated')) });
          }

          default: {
            return tagsToList({ channel, tags: ai.get('generated') });
          }
        }
      }

      case 'keywords': {
        return shapeKeywordsForAPI({ value: ai.get('generated') });
      }

      default: {
        return ai.get('generated');
      }
    }
  }

  const products = List([productId]);
  const type = getOperationType();
  const value = getOperationValue();

  return Map({ products, type, value });
}

// TODO: delete with FEATURE_FLAG_AI_V2
export function getV1ProductOperation({ ai, attribute, channel, productId }) {
  function getOperationType() {
    switch (attribute) {
      case ATTRIBUTE.DESCRIPTION: {
        return OPERATIONS[channel].DESCRIPTION.SET;
      }

      case ATTRIBUTE.TAGS: {
        return OPERATIONS[channel].TAGS.CHANGE_TO;
      }

      case ATTRIBUTE.TITLE: {
        return OPERATIONS[channel].TITLE.SET;
      }

      default: {
        return undefined;
      }
    }
  }

  function getOperationValue() {
    switch (attribute) {
      case ATTRIBUTE.TAGS: {
        return tagsToList({ channel, tags: ai.get('generated') });
      }

      default: {
        return ai.get('generated');
      }
    }
  }

  const products = List([productId]);
  const type = getOperationType();
  const value = getOperationValue();

  return Map({ ai: Map({ original: ai.get('original') }), products, type, value });
}

// TODO: delete with FEATURE_FLAG_AI_V2
export function getProductValue({ attribute, channel, operation, product, response }) {
  switch (attribute) {
    case ATTRIBUTE.TAGS: {
      switch (operation) {
        case AI_OPERATION.ADD_MAXIMUM: {
          return product.getIn([FORM.TAGS, VALUE])
            .mergeWith(keepOld, response)
            .slice(0, MAX_NUMBER_OF_TAGS[channel]);
        }

        default: {
          return response;
        }
      }
    }

    default: {
      return response;
    }
  }
}
